import { StatusBadgeTone } from 'src/app/ui/status-badge/status-badge.component';

/**
 * Catalog lifecycle states.
 *
 * The API's vocabulary and the user's do not match: a catalog whose status is
 * `Approved` is shown as "Published". The label therefore comes from i18n
 * (`Catalogs.values.*`) rather than from the raw value, and only the tone
 * mapping lives here.
 *
 * Mirrors `features/produits/utils/product-status.ts` — mapping a domain status
 * onto a badge tone belongs to the feature, so `ui-status-badge` stays ignorant
 * of every feature's vocabulary.
 */
const CATALOG_STATUS_TONES: Record<string, StatusBadgeTone> = {
  approved: 'success', // shown as "Published"
  draft: 'draft',
  inactive: 'destructive',
};

/** i18n keys under `Catalogs.values` are capitalised. */
const CATALOG_STATUS_I18N_KEYS: Record<string, string> = {
  approved: 'Approved',
  draft: 'Draft',
  inactive: 'Inactive',
};

const I18N_PREFIX = 'Catalogs.values';

function normalize(status?: string | null): string {
  return (status ?? '').trim().toLowerCase();
}

/** True when the catalog carries a status we can render a badge for. */
export function hasCatalogStatus(status?: string | null): boolean {
  return normalize(status) in CATALOG_STATUS_TONES;
}

/** Badge tone for a catalog status; unknown values fall back to `undefined`. */
export function catalogStatusTone(status?: string | null): StatusBadgeTone {
  return CATALOG_STATUS_TONES[normalize(status)] ?? 'undefined';
}

/**
 * Translation key for a catalog status, casing-normalised. Unknown values
 * resolve to the raw status so the UI degrades to the API value rather than
 * printing a missing-key path.
 */
export function catalogStatusI18nKey(status?: string | null): string {
  const key = CATALOG_STATUS_I18N_KEYS[normalize(status)];
  return key ? `${I18N_PREFIX}.${key}` : (status ?? '');
}
